import { useState, type FormEvent } from "react";
import {
  CheckCircle2,
  Loader2,
  Plus,
  Send,
  Trash2,
  XCircle,
} from "lucide-react";

import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select";
import { Switch } from "@/components/ui/Switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/Table";
import { Textarea } from "@/components/ui/Textarea";
import { useToast } from "@/components/ui/Toast";
import {
  useChannelDeliveries,
  useChannels,
  useCreateChannel,
  useDeleteChannel,
  useTestChannel,
} from "@/hooks/useChannels";
import type {
  ChannelConfig,
  ChannelKind,
  EmailChannelConfig,
  TelegramChannelConfig,
} from "@/lib/api";

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString("uk-UA");
  } catch {
    return iso;
  }
}

export default function ChannelsPage(): JSX.Element {
  const { toast } = useToast();
  const channelsQ = useChannels();
  const createMut = useCreateChannel();
  const deleteMut = useDeleteChannel();
  const testMut = useTestChannel();
  const [onlyFailures, setOnlyFailures] = useState(false);
  const deliveriesQ = useChannelDeliveries(onlyFailures);

  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [kind, setKind] = useState<ChannelKind>("telegram");
  const [isActive, setIsActive] = useState(true);
  const [tgChatId, setTgChatId] = useState("");
  const [tgBotToken, setTgBotToken] = useState("");
  const [emailTo, setEmailTo] = useState("");
  const [emailCc, setEmailCc] = useState("");
  const [formError, setFormError] = useState<string | null>(null);

  function reset(): void {
    setName("");
    setKind("telegram");
    setIsActive(true);
    setTgChatId("");
    setTgBotToken("");
    setEmailTo("");
    setEmailCc("");
    setFormError(null);
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setFormError(null);
    let config: ChannelConfig;
    if (kind === "telegram") {
      if (!tgChatId.trim()) {
        setFormError("Вкажіть chat_id для Telegram.");
        return;
      }
      const cfg: TelegramChannelConfig = { chat_id: tgChatId.trim() };
      if (tgBotToken.trim()) cfg.bot_token = tgBotToken.trim();
      config = cfg;
    } else {
      const to = emailTo
        .split(/\r?\n/)
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
      if (to.length === 0) {
        setFormError("Вкажіть принаймні одну email-адресу.");
        return;
      }
      const cc = emailCc
        .split(/\r?\n/)
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
      const cfg: EmailChannelConfig = { to_addrs: to };
      if (cc.length > 0) cfg.cc_addrs = cc;
      config = cfg;
    }
    try {
      await createMut.mutateAsync({
        name: name.trim(),
        kind,
        config,
        is_active: isActive,
      });
      toast({ title: "Канал створено", variant: "success" });
      setOpen(false);
      reset();
    } catch (err: unknown) {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? "Не вдалося створити канал.";
      setFormError(detail);
    }
  }

  async function onTest(id: number): Promise<void> {
    try {
      const res = await testMut.mutateAsync(id);
      toast({
        title: res.ok ? "Тест відправлено" : "Помилка тесту",
        description: res.detail,
        variant: res.ok ? "success" : "destructive",
      });
    } catch {
      toast({ title: "Тест не вдався", variant: "destructive" });
    }
  }

  async function onDelete(id: number): Promise<void> {
    if (!window.confirm("Видалити канал?")) return;
    try {
      await deleteMut.mutateAsync(id);
      toast({ title: "Канал видалено", variant: "success" });
    } catch {
      toast({ title: "Не вдалося видалити", variant: "destructive" });
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Канали сповіщень</h1>
          <p className="text-sm text-muted-foreground">
            Telegram / Email канали для надсилання інцидентів.
          </p>
        </div>
        <Button onClick={() => setOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Додати канал
        </Button>
      </div>

      <div className="rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Назва</TableHead>
              <TableHead>Тип</TableHead>
              <TableHead>Статус</TableHead>
              <TableHead className="text-right">Дії</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {channelsQ.isLoading ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center text-sm text-muted-foreground">
                  Завантаження…
                </TableCell>
              </TableRow>
            ) : !channelsQ.data || channelsQ.data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center text-sm text-muted-foreground">
                  Каналів ще немає.
                </TableCell>
              </TableRow>
            ) : (
              channelsQ.data.map((c) => (
                <TableRow key={c.id}>
                  <TableCell className="font-medium">{c.name}</TableCell>
                  <TableCell>{c.kind}</TableCell>
                  <TableCell>
                    {c.is_active ? (
                      <Badge variant="success">on</Badge>
                    ) : (
                      <Badge variant="secondary">off</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onTest(c.id)}
                        disabled={testMut.isPending}
                      >
                        <Send className="mr-1 h-4 w-4" />
                        Тест
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => onDelete(c.id)}
                        aria-label="Видалити"
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <div className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold">Останні доставки</h2>
            <p className="text-sm text-muted-foreground">
              Статуси надсилання сповіщень у канали (успіх / помилка).
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Label htmlFor="only-failures">Тільки помилки</Label>
            <Switch
              id="only-failures"
              checked={onlyFailures}
              onCheckedChange={setOnlyFailures}
            />
          </div>
        </div>

        <div className="rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Час</TableHead>
                <TableHead>Канал</TableHead>
                <TableHead>Подія</TableHead>
                <TableHead>Статус</TableHead>
                <TableHead>Деталі</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {deliveriesQ.isLoading ? (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    className="text-center text-sm text-muted-foreground"
                  >
                    Завантаження…
                  </TableCell>
                </TableRow>
              ) : !deliveriesQ.data || deliveriesQ.data.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    className="text-center text-sm text-muted-foreground"
                  >
                    {onlyFailures
                      ? "Помилок доставки немає."
                      : "Доставок ще немає."}
                  </TableCell>
                </TableRow>
              ) : (
                deliveriesQ.data.map((d) => (
                  <TableRow key={d.id}>
                    <TableCell className="whitespace-nowrap">
                      {formatDate(d.created_at)}
                    </TableCell>
                    <TableCell>
                      {d.channel_name ??
                        (d.channel_id != null ? `#${d.channel_id}` : "—")}
                    </TableCell>
                    <TableCell>{d.event ?? "—"}</TableCell>
                    <TableCell>
                      {d.ok ? (
                        <span className="inline-flex items-center gap-1 text-green-600">
                          <CheckCircle2 className="h-4 w-4" /> OK
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-destructive">
                          <XCircle className="h-4 w-4" /> FAIL
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="max-w-[360px] truncate text-xs text-destructive">
                      {d.error ?? ""}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Новий канал сповіщень</DialogTitle>
            <DialogDescription>
              Виберіть тип і заповніть параметри доставки.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="ch-name">Назва</Label>
              <Input
                id="ch-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Тип</Label>
              <Select
                value={kind}
                onValueChange={(v) => setKind(v as ChannelKind)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="telegram">Telegram</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {kind === "telegram" ? (
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label htmlFor="tg-chat">Chat ID</Label>
                  <Input
                    id="tg-chat"
                    value={tgChatId}
                    onChange={(e) => setTgChatId(e.target.value)}
                    placeholder="-100123456789"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="tg-token">Bot Token (опційно)</Label>
                  <Input
                    id="tg-token"
                    value={tgBotToken}
                    onChange={(e) => setTgBotToken(e.target.value)}
                    placeholder="Якщо не вказано — використовується дефолтний"
                  />
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label htmlFor="em-to">To (по одній адресі на рядок)</Label>
                  <Textarea
                    id="em-to"
                    rows={3}
                    value={emailTo}
                    onChange={(e) => setEmailTo(e.target.value)}
                    placeholder="ops@example.com"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="em-cc">CC (опційно)</Label>
                  <Textarea
                    id="em-cc"
                    rows={2}
                    value={emailCc}
                    onChange={(e) => setEmailCc(e.target.value)}
                  />
                </div>
              </div>
            )}

            <div className="flex items-center justify-between rounded-md border p-3">
              <Label htmlFor="ch-active">Активний</Label>
              <Switch
                id="ch-active"
                checked={isActive}
                onCheckedChange={setIsActive}
              />
            </div>

            {formError ? (
              <p className="text-sm text-destructive">{formError}</p>
            ) : null}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpen(false)}
              >
                Скасувати
              </Button>
              <Button type="submit" disabled={createMut.isPending}>
                {createMut.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Збереження…
                  </>
                ) : (
                  "Створити"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
